import Head from "next/head";
import {
    Collection,
    ContentResponse,
    UserResponse,
    CertificateResponse,
    ArticlePreviewResponse,
    CertificateType,
    PublicAboutPageResponse,
    ProfileUserResponse,
    PublicUserResponse,
    PublicCollectionResponse,
    PublicContentResponse,
    ShareStatus,
    UserAccountType
} from "@authory-packages/core";
import { getContentData, truncateClipWord } from "../../utils/article-text-utils";
import { DEFAULT_OG_IMAGE } from "../../types/defaultOgImage";
import { GoogleAnalyticsScript } from "../../tracking/GoogleAnalytics/GoogleAnalyticsScript";
import { getCollectionOGDisplayImageUrl } from "../../utils/getCollectionDisplayImageUrl";
import { getProfileOGDisplayImageUrl } from "../../utils/getPortfolioDisplayImageUrl";
import { CookieYesBanner } from "../CookieYesBanner";
import { useContext } from "react";
import { DomainContext, HostContext } from "../../context/domainContext";
import { RequestHost } from "../../types/headers";
import { getDisplayName } from "../../utils/getDisplayName";
import { getTwitterProfileComponent } from "../../utils/getTwitterProfileComponent";
import { getCertificateSeasonLabel, getCertificateTypeLabel } from "../../utils/getCertificateLabel";
import { ItemUrlContext } from "../V3ProfileList/V3ProfileList.utils";
import { useRouter } from "next/router";
import { canUseProFeature } from "../../utils/canUseProFeature";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { getSelfCanonicalUrl } from "../../utils/getSelfCanonicalUrl";
import { useRegisterAuthorGoogleAnalytics } from "../../context/authorGoogleAnalyticsContext";
import { getProfileDefaultDescription, getProfileDefaultTitle } from "../../utils/profileSeo";
import {
    buildAboutPageStructuredData,
    buildCollectionPageStructuredData,
    buildContentItemStructuredData,
    buildProfilePageStructuredData,
    StructuredDataCollection,
    StructuredDataItem,
    StructuredDataPage
} from "../../utils/structuredData";

function first250TextChars(htmlString: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // Extract visible text content
    const text = (doc.body?.textContent || "").replace(/\s+/g, " ").trim();

    return text.slice(0, 250);
}

/**
 * Removes html tags from user entered text, e.g. an about page title saved as "<h1>About</h1>".
 * @param text The text
 * @returns The text without anything shaped like a tag, trimmed
 */
function stripTags(text: string) {
    return text.replace(/<\/?[a-z][^>]*>/gi, "").trim();
}

/**
 * Derives the twitter handle from a profile url.
 *
 * @param url The twitter profile url, with or without a protocol
 * @returns The handle prefixed with "@", or "@Authory" when it cannot be derived
 */
function handleFromTwitterUrl(url: string) {
    try {
        const path = new URL(url, "https://twitter.com").pathname;

        if (!path) return "@Authory";

        const idx = path.lastIndexOf("/");

        if (idx >= 0) {
            return `@${path.substring(idx + 1)}`;
        } else {
            return `@${path}`;
        }
    } catch {
        return "@Authory";
    }
}

export const getTitle = (
    target: SEO_TYPE,
    user: UserResponse | ProfileUserResponse | PublicUserResponse,
    article?: ContentResponse | ArticlePreviewResponse | PublicContentResponse,
    collection?: Collection | PublicCollectionResponse,
    certificate?: CertificateResponse,
    aboutPage?: PublicAboutPageResponse
) => {
    if (target === SEO_TYPE.PROFILE && "ogTitle" in user && user.ogTitle && user.ogTitle.trim() !== "") {
        return user.ogTitle;
    }

    if (target === SEO_TYPE.ABOUT_PAGE && aboutPage) {
        return stripTags(aboutPage.title || "") || stripTags(aboutPage.name || "");
    }

    if (target === SEO_TYPE.ARTICLE && article) {
        if (!!article.ogTitle?.length) {
            return `${article.ogTitle}`;
        }

        if (!!article.title?.length) {
            const outlet = !article.publishedOnAuthory && article.sourceName ? ` · ${article.sourceName}` : "";

            return `${article.title}${outlet}`;
        }

        if (!!article.ogDescription?.length) {
            return `${truncateClipWord(article.ogDescription, 20)}`;
        }

        if (!!article.description?.length) {
            return `${truncateClipWord(article.description, 20)}`;
        }

        return `${getContentData(article.type).label} on ${article.sourceName}`;
    }

    if (target === SEO_TYPE.COLLECTION && collection) {
        return !!collection.label ? `${collection.label}` : "Collection details";
    }

    if (target === SEO_TYPE.CERTIFICATE && certificate) {
        return `${getDisplayName(user)}: ${getCertificateTypeLabel(certificate.type)} - ${getCertificateSeasonLabel(certificate.season)}`;
    }

    if (target === SEO_TYPE.PROFILE && "typeCounts" in user) {
        return getProfileDefaultTitle(user);
    }

    return `${getDisplayName(user)}'s Portfolio`;
};

export const getDescription = (
    target: SEO_TYPE,
    user: UserResponse | ProfileUserResponse | PublicUserResponse,
    article?: ContentResponse | ArticlePreviewResponse | PublicContentResponse,
    collection?: Collection | PublicCollectionResponse,
    certificate?: CertificateResponse,
    aboutPage?: PublicAboutPageResponse
) => {
    if (target === SEO_TYPE.PROFILE && "ogDescription" in user && user.ogDescription && user.ogDescription.trim() !== "") {
        return user.ogDescription;
    }

    if (target === SEO_TYPE.ABOUT_PAGE && aboutPage) {
        try {
            return first250TextChars(aboutPage?.html || "");
        } catch {
            return "";
        }
    }

    if (target === SEO_TYPE.ARTICLE && article) {
        return article.ogDescription || article.description || "";
    }

    if (target === SEO_TYPE.COLLECTION) {
        return collection?.description || "";
    }

    if (target === SEO_TYPE.CERTIFICATE && certificate) {
        switch (certificate.type) {
            case CertificateType.experienceLeader:
                return "This certificate proves that the holder's experience in writing articles is significantly higher than the work experience of an average writer.";
            case CertificateType.humanWriter:
                return "This certificate proves that the holder's articles are written with a high degree of certainty by a human, not by artificial intelligence.";
            case CertificateType.longFormProfessional:
                return "This certificate proves that the holder is significantly more experienced writing long-form articles than the average writer.";
            case CertificateType.prolificExpert:
                return "This certificate proves that the holder is significantly more experienced in creating a continuous stream of articles than the average writer.";
            case CertificateType.socialMediaSpecialist:
                return "This certificate proves that the holder's articles receive significantly higher engagement on social media compared to the average article.";
            default:
                return "";
        }
    }

    if (target === SEO_TYPE.PROFILE && "typeCounts" in user) {
        return getProfileDefaultDescription(user);
    }

    return `Find ${getDisplayName(
        user
    ).trim()}'s content all in one single place and follow all new content pieces via email, regardless where they are published.`;
};

const getOgType = (target: SEO_TYPE) => {
    switch (target) {
        case SEO_TYPE.PROFILE:
            return "profile";
        default:
            return "website";
    }
};

export const getImage = (
    target: SEO_TYPE,
    user: UserResponse | ProfileUserResponse | PublicUserResponse,
    article?: ContentResponse | ArticlePreviewResponse | PublicContentResponse,
    collection?: Collection | PublicCollectionResponse,
    certificate?: CertificateResponse
) => {
    if (target === SEO_TYPE.PROFILE && "ogCoverStyle" in user) {
        return getProfileOGDisplayImageUrl(user);
    }

    if (target === SEO_TYPE.ARTICLE) {
        if (article?.previewImage) {
            return article.previewImage;
        } else {
            return "";
        }
    }

    if (target === SEO_TYPE.COLLECTION && collection) {
        return getCollectionOGDisplayImageUrl(collection);
    }

    if (target === SEO_TYPE.CERTIFICATE && certificate) {
        return `${process.env.NEXT_PUBLIC_CERTIFICATES_S3_BUCKET}/certificates/${certificate.uid}/og`;
    }

    return DEFAULT_OG_IMAGE;
};

const getImageWidth = (target: SEO_TYPE, article?: ContentResponse | PublicContentResponse) => {
    if (article?.previewImageWidth) return article.previewImageWidth.toString();
    if (target === SEO_TYPE.CERTIFICATE) return "2000";
};

const getImageHeight = (target: SEO_TYPE, article?: ContentResponse | PublicContentResponse) => {
    if (article?.previewImageHeight) return article.previewImageHeight.toString();
    if (target === SEO_TYPE.CERTIFICATE) return "1344";
};

/**
 * Resolves the portfolio root that canonical urls are built on, the active custom domain wins over the host serving the page.
 * @param user The portfolio owner
 * @param isMainDomain Whether the page is served from the main domain
 * @param host The host serving the page
 * @returns The portfolio root url, without a trailing slash
 */
function getDomainUrl(user: UserResponse | ProfileUserResponse | PublicUserResponse, isMainDomain: boolean, host: string | null) {
    if (user.customDomain) return `https://${user.customDomain}`;

    return isMainDomain ? `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${user.slug}` : `https://${host}`;
}

const getUrl = (
    target: SEO_TYPE,
    context: ItemUrlContext,
    user: UserResponse | ProfileUserResponse | PublicUserResponse,
    article?: ContentResponse | PublicContentResponse,
    collection?: Collection | PublicCollectionResponse,
    collectionCanonicalSlug?: string,
    certificate?: CertificateResponse,
    isMainDomain = true,
    host: string | null = null,
    aboutPage?: PublicAboutPageResponse
) => {
    const domain = getDomainUrl(user, isMainDomain, host);

    if (target === SEO_TYPE.ABOUT_PAGE && aboutPage) {
        return `${domain}${APPLICATION_ROUTES.ABOUT_PAGE.replace("/[author]", "").replace("[aboutPageSlug]", aboutPage.slug)}`;
    }

    // An empty return means no valid address could be derived, the caller falls back to the page url
    if (target === SEO_TYPE.ARTICLE && article) {
        if (!article?.publishedOnAuthory) return article?.originalUrl || "";

        if (context === ItemUrlContext.profile) {
            return `${domain}${APPLICATION_ROUTES.PROFILE_CONTENT_ITEM_PAGE.replace("/[author]", "").replace("[contentItemAccessKey]", article.canonicalSlug)}`;
        } else if (context === ItemUrlContext.collection) {
            if (!collectionCanonicalSlug) return "";

            return `${domain}${APPLICATION_ROUTES.COLLECTION_CONTENT_ITEM_PAGE.replace("/[author]", "")
                .replace("[collectionAccessKey]", collectionCanonicalSlug)
                .replace("[contentItemAccessKey]", article.canonicalSlug)}`;
        } else {
            if (!article.customShareLink) return "";

            return `${domain}${APPLICATION_ROUTES.CONTENT_ITEM_PAGE.replace("/[author]", "").replace("[contentItemAccessKey]", article.customShareLink)}`;
        }
    }

    if (target === SEO_TYPE.PROFILE) {
        return domain;
    }

    if (target === SEO_TYPE.COLLECTION && collection) {
        if (context === ItemUrlContext.profile) {
            return `${domain}${APPLICATION_ROUTES.PROFILE_COLLECTION_PAGE.replace("/[author]", "").replace("[collectionAccessKey]", collection.canonicalSlug)}`;
        }

        if (!collection.customShareLink) return "";

        return `${domain}${APPLICATION_ROUTES.COLLECTION_PAGE.replace("/[author]", "").replace("[collectionAccessKey]", collection.customShareLink)}`;
    }

    if (target === SEO_TYPE.CERTIFICATE && certificate) {
        return `${domain}/certificate/${certificate.uid}`;
    }

    return domain;
};

export enum SEO_TYPE {
    PROFILE = "PROFILE",
    ABOUT_PAGE = "ABOUT_PAGE",
    ARTICLE = "ARTICLE",
    COLLECTION = "COLLECTION",
    CERTIFICATE = "CERTIFICATE"
}

/**
 * Builds the JSON-LD graph for a public page of a published, personal portfolio.
 * @param target The page type
 * @param context Whether the page sits under the portfolio or is a standalone share
 * @param user The portfolio owner
 * @param page The canonical url, portfolio root and title of the page
 * @param article The content item on an item page
 * @param collection The collection on a collection page
 * @param items The items listed on the portfolio page
 * @param collections The collections shown on the portfolio page
 * @returns The JSON-LD document, or null when the page gets none
 */
function getStructuredData(
    target: SEO_TYPE,
    context: ItemUrlContext,
    user: UserResponse | ProfileUserResponse | PublicUserResponse,
    page: Omit<StructuredDataPage, "user">,
    article?: ContentResponse | PublicContentResponse,
    collection?: Collection | PublicCollectionResponse,
    items: StructuredDataItem[] = [],
    collections: StructuredDataCollection[] = []
) {
    // A business parent lists a team's work, it is not one person
    if (!("bio" in user) || user.profileShareStatus !== ShareStatus.Published || user.type === UserAccountType.BusinessParent) return null;

    const pageWithUser = { ...page, user };
    const isInProfile = context === ItemUrlContext.profile;

    switch (target) {
        case SEO_TYPE.PROFILE:
            return buildProfilePageStructuredData(pageWithUser, items, collections);
        case SEO_TYPE.ABOUT_PAGE:
            return buildAboutPageStructuredData(pageWithUser);
        case SEO_TYPE.COLLECTION:
            return collection ? buildCollectionPageStructuredData(pageWithUser, collection, isInProfile) : null;
        case SEO_TYPE.ARTICLE:
            return article ? buildContentItemStructuredData(pageWithUser, article, isInProfile) : null;
        default:
            return null;
    }
}

type OGTagsProps = {
    user: UserResponse | ProfileUserResponse | PublicUserResponse;
    heading?: string;
    article?: ContentResponse | PublicContentResponse;
    target: SEO_TYPE;
    collection?: Collection | PublicCollectionResponse;
    certificate?: CertificateResponse;
    aboutPage?: PublicAboutPageResponse;
    skipCookieYes?: boolean;
    context: ItemUrlContext;
    forceNoIndex?: boolean;
    items?: StructuredDataItem[];
    collections?: StructuredDataCollection[];
};

export const OGTags = ({
    user,
    heading,
    target,
    article,
    collection,
    certificate,
    skipCookieYes = false,
    aboutPage,
    context,
    forceNoIndex = false,
    items,
    collections
}: OGTagsProps) => {
    const defaultTwitter = "@Authory";
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;
    const host = useContext(HostContext);
    const router = useRouter();
    const collectionCanonicalSlug = router.query.collectionAccessKey as string;
    const isMainDomain = requestHostHeader === RequestHost.MAINDOMAIN;

    const description = getDescription(target, user, article, collection, certificate, aboutPage);
    const title = getTitle(target, user, article, collection, certificate, aboutPage);
    const image = getImage(target, user, article, collection, certificate);
    const type = getOgType(target);
    const imageWidth = getImageWidth(target, article);
    const imageHeight = getImageHeight(target, article);

    const twitterProfileComponent = getTwitterProfileComponent(user);
    const twitter =
        twitterProfileComponent && twitterProfileComponent.url?.length ? handleFromTwitterUrl(twitterProfileComponent.url) : defaultTwitter;

    const derivedUrl = getUrl(target, context, user, article, collection, collectionCanonicalSlug, certificate, isMainDomain, host, aboutPage);

    // Without a derivable address the page is still its own canonical, never an empty href
    const url = derivedUrl || getSelfCanonicalUrl(router.asPath, isMainDomain ? undefined : `https://${host}`);

    const isArticleSeoHidden = !!article && !article.publishedOnAuthory && article.seoHidden;
    // Portfolio subdomains duplicate the main domain portfolio and are not a sold surface
    const isSubdomainHost = requestHostHeader === RequestHost.SUBDOMAIN;
    const isNoIndex =
        forceNoIndex || isSubdomainHost || !user.planSettings.allowSEOIndexing || target === SEO_TYPE.CERTIFICATE || isArticleSeoHidden;
    const robots = isNoIndex ? "noindex, follow" : "index, follow, max-image-preview:large";

    const structuredData = isNoIndex
        ? null
        : getStructuredData(
              target,
              context,
              user,
              {
                  pageUrl: url,
                  selfUrl: getSelfCanonicalUrl(router.asPath, isMainDomain ? undefined : `https://${host}`),
                  homeUrl: getDomainUrl(user, isMainDomain, host),
                  isMainDomain,
                  title
              },
              article,
              collection,
              items,
              collections
          );

    const hasAuthorGoogleAnalytics = !!user.googleAnalyticsToken?.length && canUseProFeature(user.planSettings);
    useRegisterAuthorGoogleAnalytics(hasAuthorGoogleAnalytics);

    return (
        <>
            <Head>
                {heading ? <title>{heading} – Authory</title> : <title>{title}</title>}

                <meta name="description" content={description} />
                <meta property="og:description" content={description} />
                <meta name="twitter:description" content={description} />

                {user.googleSearchConsoleToken?.length && canUseProFeature(user.planSettings) && (
                    <meta name="google-site-verification" content={user.googleSearchConsoleToken} />
                )}

                <meta property="og:title" content={title} />
                <meta name="twitter:title" content={title} />

                <meta property="og:image" content={image} />
                <meta property="og:image:secure" content={image} />
                <meta property="og:image:secure_url" content={image} />
                {!!imageWidth && <meta property="og:image:width" content={imageWidth} />}
                {!!imageHeight && <meta property="og:image:height" content={imageHeight} />}
                <meta property="og:url" content={url} />
                <meta property="og:type" content={type} />
                <meta property="og:first_name" content={user.firstName} />
                <meta property="og:last_name" content={user.lastName} />
                <meta property="og:site_name" content="Authory" />
                <meta name="twitter:card" content="summary_large_image" />
                <link rel="canonical" href={url} />
                <meta name="twitter:image" content={image} />
                <meta name="twitter:site" content={twitter} />
                <meta name="twitter:creator" content={twitter} />
                <meta name="robots" content={robots} />
                {structuredData && (
                    <script type="application/ld+json" key="structured-data">
                        {JSON.stringify(structuredData)}
                    </script>
                )}
            </Head>
            {isMainDomain && !skipCookieYes && <CookieYesBanner />}
            {hasAuthorGoogleAnalytics && <GoogleAnalyticsScript user={user} gaId={user.googleAnalyticsToken} />}
        </>
    );
};
